/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the class responsible for managing the application theme.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create a theme from theme configuration.
 */
import { createMuiTheme } from '@material-ui/core/styles';

/**
 * Typings
 */
import { IThemeOptions, ITheme } from './theme';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface ICreateThemeLoaderOptions<ModuleType> {
    load: () => Promise<ModuleType>;
    selector?: (importedModule: ModuleType) => IThemeOptions;
}

type IThemeLoader = () => Promise<IThemeOptions>;

type IThemeSubscription = (theme: ITheme) => void;

interface IThemeManagerOptions {
    defaultThemeKey?: string;
    initialThemeOptions: IThemeOptions;
    themeLoaders?: Record<string, IThemeLoader>;
}

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

/**
 * This function acts as the default function for loading theme configuration from
 * a dynamically loaded module.
 * @param m module
 */
export const DEFAULT_THEME_OPTIONS_SELECTOR = (m: any) => m.default as IThemeOptions;

/**
 * This function uses the provided options to create a theme loader.
 * @param options theme loader options.
 */
export const createThemeLoader = <ModuleType>(options: ICreateThemeLoaderOptions<ModuleType>) => {

    // check that minimum parameter requirements are met.
    if (!options || !options.load) {
        throw new Error(`A load function is required to create a theme loader`);
    }

    // use provided theme configuration selector function or fallback to the default.
    const selector = options.selector ? options.selector : DEFAULT_THEME_OPTIONS_SELECTOR;

    // return async function that will lazy load the desired theme.
    return async () => {
        // load the requested module.
        const loadedModule: ModuleType = await options.load();

        // select the theme configuration from the module.
        const themeOptions = await selector(loadedModule);

        // check to make sure that configuration was found.
        if (!themeOptions) {
            throw new Error(`A load function is required to create a theme loader`);
        }

        // return configuration.
        return themeOptions;
    }
}

/**
 * The default theme key.
 */
export const DEFAULT_THEME_KEY = 'DEFAULT';

/**
 * The default theme loader
 */
export const DEFAULT_THEME_LOADER = createThemeLoader({
    load: () => import('./theme/defaultTheme')
});

/**
 * This function creates a theme from the provided theme configuration.
 * @param themeOptions Theme configuration.
 */
export const createTheme = (themeOptions: IThemeOptions): ITheme => {
    const theme: ITheme =  {
        ...createMuiTheme(themeOptions),
        test: themeOptions.test ? themeOptions.test : 'test'
    };

    return theme;
};

/**
 * This theme manages the loading and swapping of themes in the application.
 */
class ThemeManager {
    /**
     * Selected theme.
     */
    private theme: ITheme;

    /**
     * Available theme loaders.
     */
    private themeLoaders: Record<string, IThemeLoader>;

    /**
     * Default theme key to use when fallback is required.
     */
    private defaultThemeKey: string;

    /**
     * Collections of subscriptions to be updated on theme change.
     */
    private subscriptions: IThemeSubscription[];

    constructor(options?: IThemeManagerOptions)
    {
        // Set options provided.
        if (options) {
            if (options.defaultThemeKey) {
                this.defaultThemeKey = options.defaultThemeKey;
            }

            if (options.themeLoaders) {
                this.themeLoaders = options.themeLoaders;
            }

            if (options.initialThemeOptions) {
                this.theme = createTheme(options.initialThemeOptions);
            }
        }

        // update default theme key if not provided.
        if (!this.defaultThemeKey) {
            this.defaultThemeKey = DEFAULT_THEME_KEY;
        }

        // update theme loader if not provided or missing default.
        if (!this.themeLoaders) {
            this.themeLoaders = {
                [this.defaultThemeKey]: DEFAULT_THEME_LOADER
            };
        }
        else if (!this.themeLoaders[this.defaultThemeKey]) {
            this.themeLoaders = {
                ...this.themeLoaders,
                [this.defaultThemeKey]: DEFAULT_THEME_LOADER
            }
        }

        // bind subscription runner scope.
        this.runSubscription = this.runSubscription.bind(this);

        // initialise subscriptions
        this.subscriptions = [];
    }

    /**
     * This function returns the current theme.
     */
    public getCurrent() {
        return this.theme;
    }

    /**
     * This function registers a subscription through the use of a callback function.
     * @param subscription subscription callback.
     */
    public subscribe(subscription: IThemeSubscription) {
        // check a callback was passed otherwise the subscription is pointless.
        if (!subscription) {
            throw new Error(`A function is required to register a subscription.`);
        }

        // register subscription.
        this.subscriptions.push(subscription);

        // return unsubscribe function.
        return () => {
            this.subscriptions = this.subscriptions.filter(sub => sub !== subscription);
        };
    }

    /**
     * This function registers / updates theme loaders.
     * @param themeLoaders theme loaders to update / register
     */
    public registerThemes(themeLoaders: Record<string, IThemeLoader>): void {
        this.themeLoaders = {
            ...this.themeLoaders,
            ...themeLoaders
        }
    }

    /**
     * This function swaps the selected theme.
     * @param themeKey theme key
     */
    public async swapTheme(themeKey?: string): Promise<void> {
        // load theme configuration
        const themeOptions = await this.loadThemeOptions(themeKey);

        // create theme from configuration
        const theme = createTheme(themeOptions);

        // set selected theme.
        this.theme = theme;

        // update all subscribed.
        this.runSubscriptions()
    }

    /**
     * This function runs all subscription callbacks.
     */
    private runSubscriptions() {
        this.subscriptions.forEach(this.runSubscription);
    }

    /**
     * This function runs a subscription callback.
     */
    private runSubscription(subscription: IThemeSubscription) {
        subscription(this.theme);
    }

    /**
     * This function loads the theme configuration requested.
     * @param themeKey theme key.
     */
    private loadThemeOptions(themeKey?: string): Promise<IThemeOptions> {
        // check if theme key provided otherwise fallback to default theme.
        if (themeKey) {
            // check theme loader exists otherwise fallback to default theme.
            if (this.themeLoaders[themeKey])
            {
                // load theme configuration.
                return this.themeLoaders[themeKey]();
            }

            // console.warn(`Theme "${themeKey}" does not exist, loading theme "${this.defaultThemeKey}".`);
        }

        // load default theme configuration.
        return this.themeLoaders[this.defaultThemeKey]();
    }
}

export default ThemeManager;


// WEBPACK FOOTER //
// ./src/style/ThemeManager.ts